import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  createBuilding,
  CreateBuildingParams,
  deleteBuilding,
  readBuildings,
  updateBuilding,
  UpdateBuildingParams,
} from 'src/api/plan';
import { Building } from 'src/api/types';
import { defaultServerError } from 'src/utils/consts';

import { messagesActions } from '../messages/slice';
import { sidesheetActions } from '../sidesheet/slice';
import { StoreState, ThunkConfig } from '../types';

import { getBuildingsState } from './selectors';

export interface FetchBuildingsParams {
  cached: boolean;
  townId: number;
}

export const fetchBuildings = createAsyncThunk<
  Building[],
  FetchBuildingsParams,
  ThunkConfig<string>
>(
  'buildings/fetchBuildings',
  async ({ cached, townId }, { getState, dispatch, rejectWithValue }) => {
    try {
      if (cached) {
        const state = getState() as StoreState;

        const { buildingsByTowns, buildingsById } = getBuildingsState(state);
        const cachedBuildingIds = buildingsByTowns.get(townId);
        if (cachedBuildingIds?.size) {
          const cachedBuildings: Building[] = [];

          for (const buildingId of cachedBuildingIds) {
            const building = buildingsById.get(buildingId);

            if (building) cachedBuildings.push(building);
          }

          if (cachedBuildings.length) return cachedBuildings;
        }
      }

      return readBuildings(townId);
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
      return rejectWithValue((e as AxiosError).message);
    }
  },
  {
    condition: ({ townId }, { getState }) => {
      const state = getState() as StoreState;
      const { currentRequests } = getBuildingsState(state);

      return !currentRequests.has(townId);
    },
  },
);

export const removeBuilding = createAsyncThunk<
  void,
  number,
  ThunkConfig<string>
>(
  'buildings/removeBuilding',
  async (id, { getState, dispatch, rejectWithValue }) => {
    try {
      const { buildingsById } = getBuildingsState(getState() as StoreState);
      const building = buildingsById.get(id);
      if (!building) return;

      await deleteBuilding(id);
      dispatch(
        messagesActions.show({
          message: `Офис ${building.address} успешно удален!`,
        }),
      );
      dispatch(sidesheetActions.close());
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
      return rejectWithValue((e as AxiosError).message);
    }
  },
);

export const addBuilding = createAsyncThunk<
  Building,
  CreateBuildingParams,
  ThunkConfig<string>
>('buildings/addTown', async (params, { dispatch, rejectWithValue }) => {
  try {
    const building = await createBuilding(params);
    dispatch(
      messagesActions.show({
        message: `Офис ${building.address} успешно добавлен!`,
      }),
    );
    dispatch(sidesheetActions.close());

    return building;
  } catch (e) {
    dispatch(messagesActions.show({ message: defaultServerError }));
    return rejectWithValue((e as AxiosError).message);
  }
});

export const changeBuilding = createAsyncThunk<
  Building,
  UpdateBuildingParams,
  ThunkConfig<string>
>('buildings/changeTown', async (params, { dispatch, rejectWithValue }) => {
  try {
    const building = await updateBuilding(params);
    dispatch(
      messagesActions.show({
        message: `Офис ${building.address} успешно обновлен!`,
      }),
    );
    dispatch(sidesheetActions.close());

    return building;
  } catch (e) {
    dispatch(messagesActions.show({ message: defaultServerError }));
    return rejectWithValue((e as AxiosError).message);
  }
});
